/**
 * RxDB database singleton for the field-zone offline collections.
 *
 * The DB is initialised lazily on first call to `getDb()`. A single instance is
 * shared for the app's lifetime; calling `getDb()` a second time returns the
 * cached instance.
 *
 * Collections:
 *   db.tasks   — local mirror of server tasks (field path only)
 *   db.outbox  — pending write operations queued while offline
 */
import { createRxDatabase, addRxPlugin } from 'rxdb'
import { RxDBAttachmentsPlugin } from 'rxdb/plugins/attachments'
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode'
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv'
import { taskSchema, expenseSchema, outboxSchema } from './schema'
import { selectOfflineStorage } from './storage'
import type { ExpenseDocument, TaskDocument, OutboxDocument } from './schema'
import type { RxDatabase, RxCollection } from 'rxdb'

addRxPlugin(RxDBMigrationSchemaPlugin)
addRxPlugin(RxDBAttachmentsPlugin)

if (import.meta.env.DEV) {
  addRxPlugin(RxDBDevModePlugin)
}

export type GranulaCollections = {
  tasks: RxCollection<TaskDocument>
  expenses: RxCollection<ExpenseDocument>
  outbox: RxCollection<OutboxDocument>
}

export type GranulaDb = RxDatabase<GranulaCollections>

let _db: GranulaDb | null = null
let _initPromise: Promise<GranulaDb> | null = null

/**
 * RxDB dev mode requires a validating storage wrapper. Dexie remains the
 * production storage; validation is development-only so schema mistakes fail
 * at the write boundary without adding validator overhead to production.
 */
export async function getDb(): Promise<GranulaDb> {
  if (_db) return _db
  if (_initPromise) return _initPromise

  _initPromise = (async () => {
    const db = await createRxDatabase<GranulaCollections>({
      name: 'granula_field',
      storage: selectOfflineStorage(
        getRxStorageDexie(),
        import.meta.env.DEV,
        ({ storage }) => wrappedValidateAjvStorage({ storage }),
      ),
    })

    await db.addCollections({
      tasks: {
        schema: taskSchema,
        // v0 → v1: added `public_id` (ADR 0007). Existing cached rows predate
        // it — null until the next pull() rehydrates them from the server.
        // v1 → v2: add user/org ownership, stable local ids, phase and sync
        // diagnostics so cached rows cannot cross sessions or lose queued edits.
        migrationStrategies: {
          1: (doc: Omit<TaskDocument, 'public_id'>) => ({ ...doc, public_id: null }),
          2: (doc: Partial<TaskDocument>) => ({
            ...doc,
            local_id: doc.local_id ?? null,
            user_id: doc.user_id ?? doc.created_by ?? 0,
            phase_id: doc.phase_id ?? null,
            sync_status: doc.sync_status ?? (doc.dirty ? 'pending_sync' : 'synced'),
            sync_error: doc.sync_error ?? null,
          }),
          3: (doc: TaskDocument & { _dirty?: boolean }) => {
            const { _dirty, ...rest } = doc
            return { ...rest, dirty: _dirty ?? doc.dirty ?? false }
          },
          4: (doc: TaskDocument) => doc,
        },
      },
      expenses: {
        schema: expenseSchema,
        migrationStrategies: {
          1: (doc: ExpenseDocument & { _dirty?: boolean }) => {
            const { _dirty, ...rest } = doc
            return { ...rest, dirty: _dirty ?? doc.dirty ?? false }
          },
          2: (doc: ExpenseDocument) => doc,
        },
      },
      outbox: {
        schema: outboxSchema,
        migrationStrategies: {
          1: (doc: Omit<OutboxDocument, 'last_error'>) => ({ ...doc, last_error: null }),
          2: (doc: OutboxDocument) => doc,
        },
      },
    })

    _db = db
    return db
  })()

  return _initPromise
}

/** Convenience: resolve the tasks collection directly. */
export async function getTasksCollection() {
  const db = await getDb()
  return db.tasks
}

/** Convenience: resolve the outbox collection directly. */
export async function getOutboxCollection() {
  const db = await getDb()
  return db.outbox
}

export async function getExpensesCollection() {
  const db = await getDb()
  return db.expenses
}
